import { useEffect, useState } from 'react';
import { IconPlus, IconRefresh, IconLoader2 } from '@tabler/icons-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import api from '../lib/api';
import PageHeader from '../components/PageHeader';
import MainCard from '../components/MainCard';

type Company = {
  entityId: number;
  uuid: string;
  entityType: 'Company' | 'Trust';
  companyCategory?: string | null;
  registeredName: string;
  registrationNumber: string | null;
  tradingName: string | null;
  incomeTaxNumber: string | null;
  vatNumber: string | null;
  ficaStatus: string;
  createdAt: string;
};

export function EntitiesPage() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    entity_type: 'Company' as 'Company' | 'Trust',
    company_category: '',
    registered_name: '',
    registration_number: '',
    trading_name: '',
    income_tax_number: '',
    vat_number: '',
  });
  const COMPANY_CATEGORIES = [
    { value: 'Pty Ltd', label: "Private Company (Pty) Ltd", format: /^\d{4}\/\d{6}\/07$/, mask: 'YYYY/XXXXXX/07' },
    { value: 'Ltd', label: "Public Company (Ltd)", format: /^\d{4}\/\d{6}\/06$/, mask: 'YYYY/XXXXXX/06' },
    { value: 'Inc', label: "Personal Liability Company (Inc)", format: /^\d{4}\/\d{6}\/21$/, mask: 'YYYY/XXXXXX/21' },
    { value: 'SOE Ltd', label: "State-Owned Company (SOE Ltd)", format: /^\d{4}\/\d{6}\/30$/, mask: 'YYYY/XXXXXX/30' },
    { value: 'NPC', label: "Non-Profit Company (NPC)", format: /^\d{4}\/\d{6}\/08$/, mask: 'YYYY/XXXXXX/08' },
    { value: 'External', label: "External Company (Foreign Branch)", format: /^\d{4}\/\d{6}\/10$/, mask: 'YYYY/XXXXXX/10' },
    { value: 'CC', label: "Close Corporation (CC)", format: /^\d{4}\/\d{6}\/23$/, mask: 'YYYY/XXXXXX/23' },
    { value: 'Cooperative', label: "Co-operative", format: /^\d{4}-\d{4}$/, mask: 'YYYY-XXXX' },
  ];

  const formatRegistrationNumber = (value: string, category: string): string => {
    if (!value || !category) return value;
    
    const selected = COMPANY_CATEGORIES.find(c => c.value === category);
    if (!selected) return value;

    const digits = value.replace(/\D/g, '');
    
    if (selected.value === 'Cooperative') {
      if (digits.length <= 4) {
        return digits;
      }
      return `${digits.slice(0, 4)}-${digits.slice(4, 8)}`;
    } else {
      if (digits.length <= 4) {
        return digits;
      }
      if (digits.length <= 10) {
        return `${digits.slice(0, 4)}/${digits.slice(4)}`;
      }
      return `${digits.slice(0, 4)}/${digits.slice(4, 10)}/${digits.slice(10, 12)}`;
    }
  };

  const loadCompanies = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/legal-entities', { params: {} });
      const data = res.data?.data ?? [];
      setCompanies(data);
    } catch (err: any) {
      console.error(err);
      setError(err.response?.data?.message || 'Failed to load companies');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCompanies();
  }, []);

  const handleCreate = async () => {
    if (!form.registered_name.trim()) {
      setCreateError('Registered name is required');
      return;
    }
    if (form.entity_type === 'Company' && !form.company_category) {
      setCreateError('Company category is required');
      return;
    }
    if (form.entity_type === 'Company' && !form.registration_number.trim()) {
      setCreateError('Registration number is required');
      return;
    }
    if (form.entity_type === 'Company' && form.registration_number) {
      const selected = COMPANY_CATEGORIES.find(c => c.value === form.company_category);
      if (selected && !selected.format.test(form.registration_number.trim())) {
        setCreateError(`Registration number format invalid for ${selected.label}. Expected format: ${selected.mask}`);
        return;
      }
    }
    if (!form.trading_name.trim()) {
      setCreateError('Trading name is required');
      return;
    }
    setCreateError(null);
    setCreating(true);
    try {
      await api.post('/legal-entities', {
        entity_type: form.entity_type,
        company_category: form.entity_type === 'Company' ? form.company_category : undefined,
        registered_name: form.registered_name.trim(),
        registration_number: form.registration_number.trim() || undefined,
        trading_name: form.trading_name.trim() || undefined,
        income_tax_number: form.income_tax_number.trim() || undefined,
        vat_number: form.vat_number.trim() || undefined,
      });
      setCreateOpen(false);
      setForm({
        entity_type: 'Company',
        company_category: '',
        registered_name: '',
        registration_number: '',
        trading_name: '',
        income_tax_number: '',
        vat_number: '',
      });
      await loadCompanies();
    } catch (err: any) {
      console.error(err);
      setCreateError(err.response?.data?.message || 'Failed to create entity');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div>
      <PageHeader
        title="Entities"
        subtitle="Manage FICA-compliant companies (Legal Entities)."
        action={
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={loadCompanies} disabled={loading}>
              <IconRefresh size={16} />
              Refresh
            </Button>
            <Button onClick={() => setCreateOpen(true)}>
              <IconPlus size={16} />
              Add Entity
            </Button>
          </div>
        }
      />

      <MainCard>
        {loading && (
          <div className="flex items-center justify-center py-16">
            <IconLoader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        )}

        {!loading && error && (
          <div className="p-4">
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          </div>
        )}

        {!loading && !error && (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Registered Name</TableHead>
                <TableHead>Trading Name</TableHead>
                <TableHead>Registration #</TableHead>
                <TableHead>VAT #</TableHead>
                <TableHead>FICA Status</TableHead>
                <TableHead>Created</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {companies.map((c) => (
                <TableRow
                  key={c.entityId}
                  className="cursor-pointer"
                  onClick={() => (window.location.href = `/compliance/entities/${c.uuid}`)}
                >
                  <TableCell>{c.registeredName}</TableCell>
                  <TableCell>{c.tradingName || '-'}</TableCell>
                  <TableCell>{c.registrationNumber || '-'}</TableCell>
                  <TableCell>{c.vatNumber || '-'}</TableCell>
                  <TableCell>{c.ficaStatus}</TableCell>
                  <TableCell>{new Date(c.createdAt).toLocaleString()}</TableCell>
                </TableRow>
              ))}
              {companies.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6}>
                    <div className="py-12 text-center">
                      <p className="text-muted-foreground">
                        No entities found yet. Add your first one.
                      </p>
                    </div>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </MainCard>

      <Dialog
        open={createOpen}
        onOpenChange={(open) => { if (!open) setCreateOpen(false); }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add Entity</DialogTitle>
          </DialogHeader>

          {createError && (
            <Alert variant="destructive" className="mb-3">
              <AlertDescription>{createError}</AlertDescription>
            </Alert>
          )}

          <div className="space-y-4">
            <div>
              <Label>Entity Type</Label>
              <Select
                value={form.entity_type}
                onValueChange={(val) => { if (val) setForm((p) => ({ ...p, entity_type: val as 'Company' | 'Trust', company_category: '' })); }}
              >
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Company">Company</SelectItem>
                  <SelectItem value="Trust">Trust</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {form.entity_type === 'Company' && (
              <div>
                <Label>Company Category</Label>
                <Select
                  value={form.company_category}
                  onValueChange={(val) => { if (val) setForm((p) => ({ ...p, company_category: val })); }}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {COMPANY_CATEGORIES.map((c) => (
                      <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground mt-1">
                  This sets the official registration document and number format
                </p>
              </div>
            )}

            <div>
              <Label>Registered Name *</Label>
              <Input
                value={form.registered_name}
                onChange={(e) => setForm((p) => ({ ...p, registered_name: e.target.value }))}
              />
            </div>

            <div>
              <Label>Registration Number {form.entity_type === 'Company' ? '*' : ''}</Label>
              <Input
                value={form.registration_number}
                onChange={(e) => {
                  const formatted = form.entity_type === 'Company' && form.company_category
                    ? formatRegistrationNumber(e.target.value, form.company_category)
                    : e.target.value;
                  setForm((p) => ({ ...p, registration_number: formatted }));
                }}
              />
              {form.entity_type === 'Company' && form.company_category && (
                <p className="text-xs text-muted-foreground mt-1">
                  Format: {COMPANY_CATEGORIES.find(c => c.value === form.company_category)?.mask}
                </p>
              )}
              {form.entity_type === 'Company' && !form.company_category && (
                <p className="text-xs text-muted-foreground mt-1">Select company category to see format</p>
              )}
            </div>

            <div>
              <Label>{form.entity_type === 'Company' ? 'Trading Name *' : 'Trust Name (if different) *'}</Label>
              <Input
                value={form.trading_name}
                onChange={(e) => setForm((p) => ({ ...p, trading_name: e.target.value }))}
              />
            </div>

            <div>
              <Label>Income Tax Number</Label>
              <Input
                value={form.income_tax_number}
                onChange={(e) => setForm((p) => ({ ...p, income_tax_number: e.target.value }))}
              />
              <p className="text-xs text-muted-foreground mt-1">Optional</p>
            </div>

            <div>
              <Label>VAT Number</Label>
              <Input
                value={form.vat_number}
                onChange={(e) => setForm((p) => ({ ...p, vat_number: e.target.value }))}
              />
              <p className="text-xs text-muted-foreground mt-1">Optional</p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)} disabled={creating}>
              Cancel
            </Button>
            <Button onClick={handleCreate} disabled={creating}>
              {creating ? <IconLoader2 size={16} className="animate-spin" /> : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
